import React, { useState, useEffect, useMemo } from "react";
import "bootstrap/dist/css/bootstrap.min.css";
import Offcanvas from "react-bootstrap/Offcanvas";
import Form from "react-bootstrap/Form";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

import {
  createMenu,
  updateMenu,
  deleteMenu,
} from "../../../services/admin/menu";

import { getAllMenuList } from "../../../services/admin/getAllMenu";
import {
  listEnums,
  createEnumEntry,
  updateEnumEntry,
  deleteEnumEntry,
} from "../../../services/admin/enumSettings";

/* ================= CONSTANTS ================= */

const STATUS = {
  PENDING: 0,
  ACTIVE: 1,
  INACTIVE: 2,
};

const emptyForm = {
  title: "",
  path: "",
  status: STATUS.ACTIVE,
  content: "",
  module: null,
  iconClass: "",
  iconImageUrl: "",
};

const emptyEnumForm = {
  type: "SYS_CAP",
  code: "",
  value: "",
  status: "active",
  countryCode: "IN",
};

/** Enum `type` values allowed for menu.module (matches backend getgloabldata). */
const ENUM_TYPES_FOR_MENU_MODULE = [
  "SYS_CAP",
  "TITLE",
  "ORG_TYPE",
  "DOC_TYPE",
  "INDUS_TYPE",
  "PRT_TYPE",
];

/* ================= TREE / DRAWER HELPERS ================= */

function SortableModuleChildRow({
  child,
  parent,
  isLast,
  onEdit,
  onDelete,
  onToggleStatus,
  renderEnumCell,
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: String(child._id) });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.55 : 1,
  };
  const branchCls = isLast ? "module-tree-branch module-tree-branch-end" : "module-tree-branch";

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`module-tree-row module-tree-row-child module-tree-grid py-2 px-2 border-bottom align-items-center ${branchCls}`}
    >
      <div className="d-flex justify-content-center align-items-center">
        <button
          type="button"
          className="btn btn-link p-0 text-muted module-tree-drag lh-1"
          style={{ cursor: "grab", minWidth: 28 }}
          aria-label="Drag to reorder"
          {...attributes}
          {...listeners}
        >
          <i className="bi bi-grip-vertical" />
        </button>
      </div>
      <div className="d-flex align-items-center gap-2 min-w-0 ps-1">
        <span className="module-tree-connector flex-shrink-0" aria-hidden />
        <i
          className="bi bi-file-earmark-text text-secondary flex-shrink-0"
          title="Child module"
        />
        <span
          className="text-truncate fw-medium flex-grow-1 min-w-0"
          title={child.title}
        >
          {child.title}
        </span>
      </div>
      <div className="small text-muted text-truncate min-w-0" title={child.path}>
        {child.path}
      </div>
      <div className="small min-w-0">{renderEnumCell(child)}</div>
      <div className="d-flex align-items-center gap-1 flex-wrap">
        <Form.Check
          type="switch"
          id={`st-child-${child._id}`}
          checked={child.status === STATUS.ACTIVE}
          onChange={() => onToggleStatus(child, true, parent)}
          aria-label="Toggle active status"
        />
        <span
          className={
            child.status === STATUS.ACTIVE ? "small text-success" : "small text-secondary"
          }
        >
          {child.status === STATUS.ACTIVE ? "Active" : "Inactive"}
        </span>
      </div>
      <div className="module-tree-actions d-flex flex-wrap gap-1 justify-content-end">
        <button
          type="button"
          className="btn btn-sm btn-outline-primary"
          style={{ borderRadius: 8 }}
          onClick={() => onEdit(parent, child)}
        >
          Edit
        </button>
        <button
          type="button"
          className="btn btn-sm btn-outline-danger"
          style={{ borderRadius: 8 }}
          onClick={() => onDelete(child._id)}
        >
          Delete
        </button>
      </div>
    </div>
  );
}

/* ================= COMPONENT ================= */

export default function Modules() {
  const [screen, setScreen] = useState("LIST"); // LIST | FORM
  const [isEdit, setIsEdit] = useState(false);

  const [data, setData] = useState([]);
  const [currentModule, setCurrentModule] = useState(null);
  /** Expanded parent menu ids (string keys). */
  const [expandedParentIds, setExpandedParentIds] = useState(() => new Set());

  const [moduleForm, setModuleForm] = useState({
    ...emptyForm,
    children: [],
  });

  const [childForm, setChildForm] = useState(emptyForm);
  const [childDrawerOpen, setChildDrawerOpen] = useState(false);
  /** When set, list-drawer parent; when null and screen===FORM, use currentModule as parent */
  const [drawerParent, setDrawerParent] = useState(null);
  const [isChildEdit, setIsChildEdit] = useState(false);
  const [currentChild, setCurrentChild] = useState(null);

  const [moduleSearch, setModuleSearch] = useState("");
  const [moduleStatusFilter, setModuleStatusFilter] = useState("");
  /** LIST screen: which primary tab is visible (enums default). */
  const [listMainTab, setListMainTab] = useState("enums");

  const dndSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } })
  );

  const [enumRows, setEnumRows] = useState([]);
  const [enumFilterType, setEnumFilterType] = useState("SYS_CAP");
  const [enumFilterCountry, setEnumFilterCountry] = useState("IN");
  const [enumFilterStatus, setEnumFilterStatus] = useState("");
  const [enumFormOpen, setEnumFormOpen] = useState(false);
  const [enumEditingId, setEnumEditingId] = useState(null);
  const [enumForm, setEnumForm] = useState(emptyEnumForm);

  /** Enums loaded for parent menu `module` dropdown (filtered by moduleEnumType). */
  const [modulePickerEnums, setModulePickerEnums] = useState([]);
  /** Enum type used to load options for the module dropdown. */
  const [moduleEnumType, setModuleEnumType] = useState("SYS_CAP");
  /** SYS_CAP id → row, for list column labels (capabilities are usually SYS_CAP). */
  const [enumByIdForList, setEnumByIdForList] = useState({});

  /* ================= LOAD LIST ================= */

  const loadModules = async () => {
    try {
      const res = await getAllMenuList();
      setData(res.menus || []);
    } catch (err) {
      console.error("LOAD MODULES ERROR:", err);
      setData([]);
    }
  };

  useEffect(() => {
    loadModules();
  }, []);

  const loadEnumCacheForModuleColumn = async () => {
    try {
      const res = await listEnums({
        type: "SYS_CAP",
        countryCode: "IN",
        status: "active",
        page: 1,
        limit: 100,
      });
      const map = {};
      (res?.data || []).forEach((e) => {
        map[String(e._id)] = e;
      });
      setEnumByIdForList(map);
    } catch {
      setEnumByIdForList({});
    }
  };

  useEffect(() => {
    loadEnumCacheForModuleColumn();
  }, []);

  useEffect(() => {
    if (screen !== "FORM") return;
    (async () => {
      try {
        const res = await listEnums({
          type: moduleEnumType,
          countryCode: "IN",
          status: "active",
          page: 1,
          limit: 100,
        });
        setModulePickerEnums(res?.data || []);
      } catch (e) {
        setModulePickerEnums([]);
      }
    })();
  }, [screen, moduleEnumType]);

  const loadEnumRows = async () => {
    try {
      const body = {
        type: enumFilterType.trim() || undefined,
        countryCode: enumFilterCountry.trim().toUpperCase() || undefined,
        status: enumFilterStatus || undefined,
        page: 1,
        limit: 100,
      };
      const res = await listEnums(body);
      setEnumRows(res?.data || []);
    } catch (err) {
      console.error("LOAD ENUMS ERROR:", err);
      setEnumRows([]);
    }
  };

  useEffect(() => {
    if (screen !== "LIST") return;
    loadEnumRows();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [screen, enumFilterType, enumFilterCountry, enumFilterStatus]);

  const closeEnumDrawer = () => {
    setEnumFormOpen(false);
    setEnumEditingId(null);
    setEnumForm(emptyEnumForm);
  };

  const openCreateEnum = () => {
    setEnumEditingId(null);
    setEnumForm({
      ...emptyEnumForm,
      type: (enumFilterType || "SYS_CAP").trim() || "SYS_CAP",
      countryCode: (enumFilterCountry || "IN").toUpperCase(),
    });
    setEnumFormOpen(true);
  };

  const openEditEnum = (row) => {
    setEnumEditingId(row._id);
    setEnumForm({
      type: row.type || "",
      code: row.code || "",
      value: row.value || "",
      status: row.status || "active",
      countryCode: (row.countryCode || "IN").toUpperCase(),
    });
    setEnumFormOpen(true);
  };

  const saveEnumRow = async () => {
    if (!enumForm.type || !enumForm.code || !enumForm.value) {
      window.alert("Type, code, and value are required.");
      return;
    }
    const payload = {
      type: enumForm.type.trim(),
      code: enumForm.code.trim(),
      value: enumForm.value.trim(),
      status: enumForm.status,
      countryCode: (enumForm.countryCode || "IN").toUpperCase(),
    };
    try {
      if (enumEditingId) {
        await updateEnumEntry(enumEditingId, payload);
      } else {
        await createEnumEntry(payload);
      }
      closeEnumDrawer();
      await loadEnumRows();
      await loadEnumCacheForModuleColumn();
    } catch (err) {
      window.alert(err?.message || "Could not save enum");
    }
  };

  const handleDeleteEnum = async (id) => {
    if (!window.confirm("Delete this enum entry?")) return;
    try {
      await deleteEnumEntry(id);
      await loadEnumRows();
      await loadEnumCacheForModuleColumn();
    } catch (err) {
      window.alert(err?.message || "Could not delete enum");
    }
  };

  /* ================= MODULE ================= */

  const openAddModule = () => {
    setIsEdit(false);
    setCurrentModule(null);
    setModuleEnumType("SYS_CAP");
    setModuleForm({ ...emptyForm, children: [] });
    setChildDrawerOpen(false);
    setDrawerParent(null);
    setScreen("FORM");
  };

  const normalizeModuleId = (m) => {
    if (m == null) return null;
    if (typeof m === "object" && m._id != null) return String(m._id);
    return String(m);
  };

  const filteredModules = useMemo(() => {
    const q = moduleSearch.trim().toLowerCase();
    return data.filter((m) => {
      if (moduleStatusFilter === "active" && m.status !== STATUS.ACTIVE)
        return false;
      if (moduleStatusFilter === "inactive" && m.status === STATUS.ACTIVE)
        return false;
      if (!q) return true;
      const pMatch =
        (m.title || "").toLowerCase().includes(q) ||
        (m.path || "").toLowerCase().includes(q);
      const cMatch = m.children?.some(
        (c) =>
          (c.title || "").toLowerCase().includes(q) ||
          (c.path || "").toLowerCase().includes(q)
      );
      return pMatch || cMatch;
    });
  }, [data, moduleSearch, moduleStatusFilter]);

  useEffect(() => {
    const q = moduleSearch.trim().toLowerCase();
    if (!q) return;
    setExpandedParentIds((prev) => {
      const next = new Set(prev);
      data.forEach((m) => {
        const hit = m.children?.some(
          (c) =>
            (c.title || "").toLowerCase().includes(q) ||
            (c.path || "").toLowerCase().includes(q)
        );
        if (hit) next.add(String(m._id));
      });
      return next;
    });
  }, [moduleSearch, data]);

  const toggleParentExpand = (id) => {
    const key = String(id);
    setExpandedParentIds((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const closeChildDrawer = () => {
    setChildDrawerOpen(false);
    setDrawerParent(null);
    setChildForm({ ...emptyForm });
    setIsChildEdit(false);
    setCurrentChild(null);
  };

  const renderModuleEnumCell = (row) => {
    const id = normalizeModuleId(row.module);
    if (!id) return <span className="text-muted">—</span>;
    const rowEnum = enumByIdForList[id];
    if (rowEnum) {
      return (
        <span className="small" title={id}>
          <span className="fw-semibold text-secondary">{rowEnum.code}</span>
          <span className="text-muted"> · </span>
          {rowEnum.value}
        </span>
      );
    }
    return (
      <span className="text-muted small" title={id}>
        {String(id).slice(0, 8)}…
      </span>
    );
  };

  const handleToggleMenuStatus = async (row, isChild, parentMod) => {
    const next =
      row.status === STATUS.ACTIVE ? STATUS.INACTIVE : STATUS.ACTIVE;
    const payload = {
      title: row.title,
      path: row.path,
      content: row.content ?? null,
      status: next,
      parent: isChild && parentMod ? parentMod._id : null,
    };
    const mid = normalizeModuleId(row.module);
    if (mid) payload.module = mid;
    try {
      await updateMenu(row._id, payload);
      await loadModules();
      if (screen === "FORM" && isEdit && currentModule) {
        const fresh = await getAllMenuList();
        const updated = (fresh.menus || []).find((m) => m._id === currentModule._id);
        setModuleForm((prev) => ({
          ...prev,
          children: updated?.children || [],
        }));
      }
    } catch (e) {
      window.alert(e?.message || "Could not update status");
    }
  };

  const getResolvedChildParent = () => {
    if (drawerParent) return drawerParent;
    if (screen === "FORM" && isEdit && currentModule) return currentModule;
    return null;
  };

  const handleChildDragEnd = async (event, parentModule) => {
    const { active, over } = event;
    if (!over || String(active.id) === String(over.id)) return;
    const list = parentModule.children || [];
    const ids = list.map((c) => String(c._id));
    const oldIndex = ids.indexOf(String(active.id));
    const newIndex = ids.indexOf(String(over.id));
    if (oldIndex < 0 || newIndex < 0) return;
    const newOrder = arrayMove(list, oldIndex, newIndex);
    const payload = {
      title: parentModule.title,
      path: parentModule.path,
      content: parentModule.content ?? null,
      status: parentModule.status,
      parent: null,
      children: newOrder.map((c) => ({
        title: c.title,
        path: c.path,
        status: c.status,
        content: c.content || null,
      })),
    };
    const modId = normalizeModuleId(parentModule.module);
    if (modId) payload.module = modId;
    try {
      await updateMenu(parentModule._id, payload);
      await loadModules();
    } catch (e) {
      window.alert(e?.message || "Reorder failed");
      await loadModules();
    }
  };

  const onGlobalChildDragEnd = (event) => {
    const { active, over } = event;
    if (!over || String(active.id) === String(over.id)) return;
    const activeId = String(active.id);
    const parentModule = data.find((m) =>
      m.children?.some((c) => String(c._id) === activeId)
    );
    if (!parentModule) return;
    handleChildDragEnd(event, parentModule);
  };

  const openEditModule = (mod) => {
    setIsEdit(true);
    setCurrentModule(mod);
    const modRef = mod.module;
    const pickedType =
      modRef && typeof modRef === "object" && modRef.type
        ? modRef.type
        : "SYS_CAP";
    setModuleEnumType(
      ENUM_TYPES_FOR_MENU_MODULE.includes(pickedType) ? pickedType : "SYS_CAP"
    );
    setModuleForm({
      title: mod.title,
      path: mod.path,
      status: mod.status ?? STATUS.ACTIVE,
      content: mod.content || "",
      module: normalizeModuleId(mod.module),
      children: mod.children || [],
    });
    setChildDrawerOpen(false);
    setDrawerParent(null);
    setScreen("FORM");
  };

  const saveModuleOLD = async () => {
    if (!moduleForm.title || !moduleForm.path) return;

    const payload = {
      title: moduleForm.title,
      path: moduleForm.path,
      content: moduleForm.content || null,
      status: moduleForm.status,
      parent: null,
    };

    try {
      if (isEdit) {
        await updateMenu(currentModule._id, payload);
      } else {
        await createMenu(payload);
      }
      await loadModules();
      setScreen("LIST");
    } catch (err) {
      console.error("SAVE MODULE ERROR:", err.message);
    }
  };

  const saveModule = async () => {
    if (!moduleForm.title || !moduleForm.path) return;

    const payload = {
      title: moduleForm.title,
      path: moduleForm.path,
      content: moduleForm.content || null,
      status: moduleForm.status,
      parent: null,
      children: moduleForm.children.map((c) => ({
        title: c.title,
        path: c.path,
        status: c.status,
        content: c.content || null,
      })),
    };

    const modId = normalizeModuleId(moduleForm.module);
    if (modId) {
      payload.module = modId;
    } else if (isEdit) {
      payload.module = null;
    }

    try {
      if (isEdit) {
        await updateMenu(currentModule._id, payload);
      } else {
        await createMenu(payload);
      }

      await loadModules();
      setScreen("LIST");
    } catch (err) {
      console.error("SAVE MODULE ERROR:", err.message);
    }
  };


  const handleDeleteModule = async (id) => {
    if (!window.confirm("Delete this module?")) return;
    await deleteMenu(id);
    loadModules();
  };

  /* ================= CHILD (drawer) ================= */

  const openAddChild = () => {
    setChildForm({ ...emptyForm });
    setIsChildEdit(false);
    setCurrentChild(null);
    setDrawerParent(null);
    setChildDrawerOpen(true);
  };

  const openAddChildFromList = (parentMod) => {
    setChildForm({ ...emptyForm });
    setIsChildEdit(false);
    setCurrentChild(null);
    setDrawerParent(parentMod);
    setChildDrawerOpen(true);
  };

  const openEditChild = (child) => {
    setIsChildEdit(true);
    setCurrentChild(child);
    setDrawerParent(null);
    setChildForm({
      title: child.title,
      path: child.path,
      status: child.status ?? STATUS.ACTIVE,
      content: child.content || "",
      module: normalizeModuleId(child.module),
      iconClass: typeof child.icon === "string" ? child.icon : "",
      iconImageUrl: child.iconImageUrl || "",
    });
    setChildDrawerOpen(true);
  };

  const openEditChildFromList = (parentMod, child) => {
    setDrawerParent(parentMod);
    setIsChildEdit(true);
    setCurrentChild(child);
    setChildForm({
      title: child.title,
      path: child.path,
      status: child.status ?? STATUS.ACTIVE,
      content: child.content || "",
      module: normalizeModuleId(child.module),
      iconClass: typeof child.icon === "string" ? child.icon : "",
      iconImageUrl: child.iconImageUrl || "",
    });
    setChildDrawerOpen(true);
  };

  const saveChild = async () => {
    if (!childForm.title || !childForm.path) return;

    if (!isEdit && screen === "FORM") {
      if (isChildEdit && currentChild?._id != null) {
        setModuleForm((prev) => ({
          ...prev,
          children: prev.children.map((c) =>
            String(c._id) === String(currentChild._id)
              ? {
                  ...c,
                  title: childForm.title,
                  path: childForm.path,
                  status: childForm.status,
                  content: childForm.content || null,
                  module: childForm.module,
                }
              : c
          ),
        }));
        closeChildDrawer();
        return;
      }
      setModuleForm((prev) => ({
        ...prev,
        children: [
          ...prev.children,
          {
            _id: Date.now(),
            title: childForm.title,
            path: childForm.path,
            status: childForm.status,
            content: childForm.content || null,
            module: childForm.module,
          },
        ],
      }));
      closeChildDrawer();
      return;
    }

    const parent = getResolvedChildParent();
    if (!parent?._id) {
      window.alert(
        "Parent module is missing. Pick a parent from the tree or save the parent module first."
      );
      return;
    }

    const payload = {
      title: childForm.title,
      path: childForm.path,
      content: childForm.content || null,
      status: childForm.status,
      parent: parent._id,
    };
    const modId = normalizeModuleId(childForm.module);
    if (modId) payload.module = modId;
    const ic = (childForm.iconClass || "").trim();
    if (ic) payload.icon = ic;
    const iu = (childForm.iconImageUrl || "").trim();
    if (iu) payload.iconImageUrl = iu;

    try {
      if (isChildEdit && currentChild?._id) {
        await updateMenu(currentChild._id, payload);
      } else {
        await createMenu(payload);
      }
      await loadModules();
      if (screen === "FORM" && isEdit && currentModule) {
        const fresh = await getAllMenuList();
        const updated = (fresh.menus || []).find((m) => m._id === currentModule._id);
        setModuleForm((prev) => ({
          ...prev,
          children: updated?.children || [],
        }));
      }
      closeChildDrawer();
    } catch (err) {
      window.alert(err?.message || String(err));
    }
  };

  const handleDeleteChild = async (childId) => {
    if (!window.confirm("Delete this child?")) return;
    if (!isEdit && screen === "FORM") {
      setModuleForm((prev) => ({
        ...prev,
        children: prev.children.filter((c) => String(c._id) !== String(childId)),
      }));
      return;
    }
    try {
      await deleteMenu(childId);
      await loadModules();
      if (screen === "FORM" && isEdit && currentModule) {
        const fresh = await getAllMenuList();
        const updated = (fresh.menus || []).find((m) => m._id === currentModule._id);
        setModuleForm((prev) => ({
          ...prev,
          children: updated?.children || [],
        }));
      }
    } catch (e) {
      window.alert(e?.message || "Delete failed");
    }
  };

  const drawerParentTitle =
    drawerParent?.title ||
    (screen === "FORM" && isEdit && currentModule?.title) ||
    moduleForm.title ||
    "—";

  const drawerPathPreview = `${drawerParentTitle}${
    childForm.title ? ` > ${childForm.title}` : " > …"
  }`;

  const enumDrawerScopePreview = enumEditingId
    ? `${enumForm.type || "—"} > ${enumForm.code || "…"}`
    : `${(enumFilterType || "—").trim() || "—"} > New enum`;

  const enumDrawerFilterHint = `${(enumFilterType || "—").trim()} · ${(enumFilterCountry || "IN").toUpperCase()}${
    enumFilterStatus ? ` · ${enumFilterStatus}` : ""
  }`;

  /* ================= UI ================= */

  return (
    <div className="container mt-3">
      <style>{`
        .module-tree-card { border-radius: 10px; overflow: hidden; }
        .module-tree-grid {
          display: grid;
          grid-template-columns: 36px minmax(200px, 1.5fr) minmax(120px, 1fr) minmax(140px, 1fr) 130px minmax(220px, auto);
          gap: 0.5rem;
          align-items: center;
        }
        .module-tree-header {
          font-size: 0.72rem;
          text-transform: uppercase;
          letter-spacing: 0.04em;
          color: #6c757d;
          font-weight: 600;
          padding: 0.65rem 0.75rem;
          background: #f8f9fa;
          border-bottom: 1px solid #dee2e6;
        }
        .module-tree-row-parent { background: #fff; }
        .module-tree-row-child {
          background: #f8fafc;
          padding-left: 0.25rem !important;
        }
        .module-tree-row-child.module-tree-grid > div:nth-child(2) {
          min-width: 0;
          overflow: hidden;
        }
        .module-tree-row:hover { background: #f1f5f9 !important; }
        .module-tree-actions { opacity: 0; transition: opacity 0.15s ease; }
        .module-tree-row:hover .module-tree-actions { opacity: 1; }
        .module-tree-branch { position: relative; }
        .module-tree-connector {
          width: 10px;
          border-left: 2px solid #cbd5e1;
          min-height: 1.25rem;
          margin-left: 4px;
        }
        .module-tree-branch-end .module-tree-connector {
          border-bottom: 2px solid #cbd5e1;
          border-bottom-left-radius: 6px;
          margin-bottom: 0.35rem;
        }
        .child-drawer-shadow { box-shadow: -8px 0 28px rgba(15, 23, 42, 0.12); }
        .list-page-tabs {
          display: flex;
          flex-wrap: wrap;
          gap: 0.5rem;
          margin-bottom: 20px;
        }
        .list-page-tab-btn {
          border: none;
          padding: 0.55rem 1.25rem;
          font-weight: 600;
          font-size: 0.9375rem;
          border-radius: 8px;
          cursor: pointer;
          transition: background 0.15s ease, color 0.15s ease, opacity 0.15s ease;
        }
        .list-page-tab-btn--active {
          background: #2563eb;
          color: #fff;
        }
        .list-page-tab-btn--inactive {
          background: #f3f4f6;
          color: #374151;
        }
        .list-page-tab-btn--inactive:hover {
          opacity: 0.92;
        }
        .list-page-tab-panel-fade {
          animation: listPageTabFade 0.2s ease;
        }
        @keyframes listPageTabFade {
          from { opacity: 0.65; }
          to { opacity: 1; }
        }
      `}</style>

      {/* ================= BREADCRUMB ================= */}
      {screen === "FORM" && (
        <nav
          aria-label="breadcrumb"
          className="px-3 py-2 border-bottom bg-light mb-3"
        >
          <ol
            className="breadcrumb mb-0 align-items-center"
            style={{ ["--bs-breadcrumb-divider"]: "'/'" }}
          >
            <li className="breadcrumb-item">
              <button
                type="button"
                className="btn btn-link p-0 text-decoration-none text-muted"
                onClick={() => setScreen("LIST")}
              >
                Modules
              </button>
            </li>
            <li
              className="breadcrumb-item active fw-semibold text-primary"
              aria-current="page"
            >
              {isEdit ? "Edit Module" : "Add Module"}
            </li>
          </ol>
        </nav>
      )}

      {/* ================= LIST ================= */}
      {screen === "LIST" ? (
        <>
          <div className="list-page-tabs" role="tablist" aria-label="Admin sections">
            <button
              type="button"
              role="tab"
              aria-selected={listMainTab === "enums"}
              id="list-tab-enums"
              className={`list-page-tab-btn ${
                listMainTab === "enums"
                  ? "list-page-tab-btn--active"
                  : "list-page-tab-btn--inactive"
              }`}
              onClick={() => setListMainTab("enums")}
            >
              Reference Enums
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={listMainTab === "modules"}
              id="list-tab-modules"
              className={`list-page-tab-btn ${
                listMainTab === "modules"
                  ? "list-page-tab-btn--active"
                  : "list-page-tab-btn--inactive"
              }`}
              onClick={() => setListMainTab("modules")}
            >
              Modules
            </button>
          </div>

          {listMainTab === "modules" ? (
            <div
              key="panel-modules"
              className="list-page-tab-panel-fade"
              role="tabpanel"
              aria-labelledby="list-tab-modules"
            >
          <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
            <h4 className="mb-0">Modules</h4>
            <button
              type="button"
              className="btn btn-primary"
              style={{ borderRadius: 8 }}
              onClick={openAddModule}
            >
              + Add Module
            </button>
          </div>

          <div className="row g-2 mb-3">
            <div className="col-md-6">
              <label className="form-label small mb-0">Search modules</label>
              <input
                className="form-control form-control-sm"
                placeholder="Title or path (includes child rows)"
                value={moduleSearch}
                onChange={(e) => setModuleSearch(e.target.value)}
              />
            </div>
            <div className="col-md-3">
              <label className="form-label small mb-0">Status</label>
              <select
                className="form-select form-select-sm"
                value={moduleStatusFilter}
                onChange={(e) => setModuleStatusFilter(e.target.value)}
              >
                <option value="">All</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
          </div>

          <DndContext
            sensors={dndSensors}
            collisionDetection={closestCenter}
            onDragEnd={onGlobalChildDragEnd}
          >
            <div className="card border module-tree-card shadow-sm">
              <div className="module-tree-grid module-tree-header">
                <span aria-hidden />
                <span>Module</span>
                <span>Path</span>
                <span>Module (enum)</span>
                <span>Status</span>
                <span className="text-end">Actions</span>
              </div>
              <div className="border-top">
                {filteredModules.length === 0 ? (
                  <div className="text-muted text-center py-4 small">
                    No modules match your filters.
                  </div>
                ) : (
                  filteredModules.map((m) => {
                    const exp = expandedParentIds.has(String(m._id));
                    const kids = m.children || [];
                    const sortableIds = kids.map((c) => String(c._id));
                    return (
                      <React.Fragment key={m._id}>
                        <div className="module-tree-row module-tree-row-parent module-tree-grid py-2 px-2 border-bottom">
                          <div className="d-flex justify-content-center">
                            <button
                              type="button"
                              className="btn btn-link p-0 lh-1 text-secondary"
                              style={{ minWidth: 28 }}
                              aria-expanded={exp}
                              aria-label={
                                kids.length
                                  ? exp
                                    ? "Collapse child modules"
                                    : "Expand child modules"
                                  : undefined
                              }
                              onClick={() =>
                                kids.length ? toggleParentExpand(m._id) : null
                              }
                              disabled={!kids.length}
                            >
                              {kids.length ? (
                                <i
                                  className={`bi ${
                                    exp ? "bi-chevron-down" : "bi-chevron-right"
                                  }`}
                                />
                              ) : (
                                <span className="invisible">·</span>
                              )}
                            </button>
                          </div>
                          <div className="d-flex align-items-center gap-2 min-w-0">
                            <i
                              className="bi bi-folder2 text-warning flex-shrink-0"
                              title="Parent module"
                            />
                            <span className="fw-semibold text-truncate" title={m.title}>
                              {m.title}
                            </span>
                          </div>
                          <div className="small text-muted text-truncate" title={m.path}>
                            {m.path}
                          </div>
                          <div>{renderModuleEnumCell(m)}</div>
                          <div className="d-flex align-items-center gap-1 flex-wrap">
                            <Form.Check
                              type="switch"
                              id={`st-parent-${m._id}`}
                              checked={m.status === STATUS.ACTIVE}
                              onChange={() => handleToggleMenuStatus(m, false, null)}
                              aria-label="Toggle active status"
                            />
                            <span
                              className={
                                m.status === STATUS.ACTIVE
                                  ? "small text-success"
                                  : "small text-secondary"
                              }
                            >
                              {m.status === STATUS.ACTIVE ? "Active" : "Inactive"}
                            </span>
                          </div>
                          <div className="module-tree-actions d-flex flex-wrap gap-1 justify-content-end">
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-success"
                              style={{ borderRadius: 8 }}
                              onClick={() => openAddChildFromList(m)}
                            >
                              + Add Child
                            </button>
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-secondary"
                              style={{ borderRadius: 8 }}
                              onClick={() => openEditModule(m)}
                            >
                              Edit
                            </button>
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-danger"
                              style={{ borderRadius: 8 }}
                              onClick={() => handleDeleteModule(m._id)}
                            >
                              Delete
                            </button>
                          </div>
                        </div>

                        {exp && kids.length > 0 && (
                          <SortableContext
                            items={sortableIds}
                            strategy={verticalListSortingStrategy}
                          >
                            {kids.map((c, idx) => (
                              <SortableModuleChildRow
                                key={c._id}
                                child={c}
                                parent={m}
                                isLast={idx === kids.length - 1}
                                onEdit={openEditChildFromList}
                                onDelete={handleDeleteChild}
                                onToggleStatus={handleToggleMenuStatus}
                                renderEnumCell={renderModuleEnumCell}
                              />
                            ))}
                          </SortableContext>
                        )}
                      </React.Fragment>
                    );
                  })
                )}
              </div>
            </div>
          </DndContext>
            </div>
          ) : (
            <div
              key="panel-enums"
              className="list-page-tab-panel-fade"
              role="tabpanel"
              aria-labelledby="list-tab-enums"
            >
          <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
            <h4 className="mb-0">Reference enums</h4>
            <button
              type="button"
              className="btn btn-primary"
              style={{ borderRadius: 8 }}
              onClick={openCreateEnum}
            >
              + Add enum
            </button>
          </div>
          <p className="text-muted small mb-3">
            Manage lookup values (for example SYS_CAP codes used for capabilities). Adjust filters
            and use Add enum to create entries.
          </p>

          <div className="row g-2 mb-3">
            <div className="col-md-3">
              <label className="form-label small mb-0">Type</label>
              <input
                className="form-control form-control-sm"
                value={enumFilterType}
                onChange={(e) => setEnumFilterType(e.target.value)}
                placeholder="e.g. SYS_CAP"
              />
            </div>
            <div className="col-md-2">
              <label className="form-label small mb-0">Country</label>
              <input
                className="form-control form-control-sm text-uppercase"
                maxLength={2}
                value={enumFilterCountry}
                onChange={(e) => setEnumFilterCountry(e.target.value)}
              />
            </div>
            <div className="col-md-2">
              <label className="form-label small mb-0">Status</label>
              <select
                className="form-select form-select-sm"
                value={enumFilterStatus}
                onChange={(e) => setEnumFilterStatus(e.target.value)}
              >
                <option value="">All</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
          </div>

          <div className="card border module-tree-card shadow-sm">
            <div className="module-tree-grid module-tree-header">
              <span aria-hidden />
              <span>Type</span>
              <span>Code</span>
              <span>Value</span>
              <span>Status</span>
              <span className="text-end">Actions</span>
            </div>
            <div className="border-top">
              {enumRows.length === 0 ? (
                <div className="text-muted text-center py-4 small">
                  No enums match the current filters.
                </div>
              ) : (
                enumRows.map((row) => (
                  <div
                    key={row._id}
                    className="module-tree-row module-tree-row-parent module-tree-grid py-2 px-2 border-bottom align-items-center"
                  >
                    <div className="d-flex justify-content-center">
                      <i
                        className="bi bi-ui-checks-grid text-secondary"
                        title="Enum entry"
                        aria-hidden
                      />
                    </div>
                    <div className="min-w-0">
                      <div className="fw-semibold text-truncate" title={row.type}>
                        {row.type}
                      </div>
                      <div className="small text-muted text-truncate">
                        {row.countryCode}
                      </div>
                    </div>
                    <div
                      className="small text-muted text-truncate font-monospace"
                      title={row.code}
                    >
                      {row.code}
                    </div>
                    <div className="small text-truncate" title={row.value}>
                      {row.value}
                    </div>
                    <div>
                      <span
                        className={
                          row.status === "active"
                            ? "small text-success fw-medium"
                            : "small text-secondary"
                        }
                      >
                        {row.status === "active"
                          ? "Active"
                          : row.status === "inactive"
                            ? "Inactive"
                            : row.status}
                      </span>
                    </div>
                    <div className="module-tree-actions d-flex flex-wrap gap-1 justify-content-end">
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-secondary"
                        style={{ borderRadius: 8 }}
                        onClick={() => openEditEnum(row)}
                      >
                        Edit
                      </button>
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-danger"
                        style={{ borderRadius: 8 }}
                        onClick={() => handleDeleteEnum(row._id)}
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
            </div>
          )}
        </>
      ) : (
        /* ================= FORM ================= */
        <>
          <div className="row">
            {/* MODULE FORM */}
            <div className="col-md-6">
              <h5>Module</h5>

              <input
                className="form-control mb-2"
                placeholder="Title"
                value={moduleForm.title}
                onChange={(e) =>
                  setModuleForm({ ...moduleForm, title: e.target.value })
                }
              />

              <input
                className="form-control mb-2"
                placeholder="Path"
                value={moduleForm.path}
                onChange={(e) =>
                  setModuleForm({ ...moduleForm, path: e.target.value })
                }
              />

              <select
                className="form-select mb-2"
                value={moduleForm.status}
                onChange={(e) =>
                  setModuleForm({
                    ...moduleForm,
                    status: Number(e.target.value),
                  })
                }
              >
                <option value={STATUS.ACTIVE}>Active</option>
                <option value={STATUS.INACTIVE}>Inactive</option>
                <option value={STATUS.PENDING}>Pending</option>
              </select>

              <div className="border rounded p-3 mb-3 bg-light">
                <label className="form-label fw-semibold mb-1">
                  Module (enum reference)
                </label>
                <p className="small text-muted mb-2">
                  Choose the enum <strong>type</strong>, then the row: stored field is{" "}
                  <strong>code</strong> (key); <strong>value</strong> is the label. Only parent
                  modules send this to the server.
                </p>
                <label className="form-label small mb-0">Enum list (type)</label>
                <select
                  className="form-select form-select-sm mb-2"
                  value={moduleEnumType}
                  onChange={(e) => {
                    const next = e.target.value;
                    setModuleEnumType(next);
                    setModuleForm((prev) => ({ ...prev, module: null }));
                  }}
                >
                  {ENUM_TYPES_FOR_MENU_MODULE.map((t) => (
                    <option key={t} value={t}>
                      {t}
                    </option>
                  ))}
                </select>
                <label className="form-label small mb-0">Module — pick code / value</label>
                <select
                  className="form-select mb-0"
                  value={moduleForm.module || ""}
                  onChange={(e) =>
                    setModuleForm({
                      ...moduleForm,
                      module: e.target.value || null,
                    })
                  }
                >
                  <option value="">— None —</option>
                  {(() => {
                    const sel = normalizeModuleId(moduleForm.module);
                    const inPicker =
                      sel &&
                      modulePickerEnums.some((r) => String(r._id) === sel);
                    if (sel && !inPicker) {
                      return (
                        <option value={sel}>
                          Current id (change enum type above if list is wrong): {sel}
                        </option>
                      );
                    }
                    return null;
                  })()}
                  {modulePickerEnums.map((row) => (
                    <option key={row._id} value={row._id}>
                      {row.code} · {row.value}
                    </option>
                  ))}
                </select>
                {modulePickerEnums.length === 0 && (
                  <div className="form-text text-warning small mt-1">
                    No active enums for this type (IN). Add rows under Reference enums below or
                    change type.
                  </div>
                )}
              </div>

              <textarea
                className="form-control"
                placeholder="Content"
                value={moduleForm.content}
                onChange={(e) =>
                  setModuleForm({ ...moduleForm, content: e.target.value })
                }
              />
            </div>

            {/* CHILD SECTION */}
            <div className="col-md-6">
              <div className="d-flex justify-content-between align-items-start mb-2">
                <div>
                  <h5 className="mb-1">Children</h5>
                  <p className="small text-muted mb-0">
                    Add or edit children in the side drawer (parent is filled in automatically).
                  </p>
                </div>
                <button
                  type="button"
                  className="btn btn-sm btn-success"
                  style={{ borderRadius: 8 }}
                  onClick={openAddChild}
                >
                  + Add Child
                </button>
              </div>

              <table className="table table-sm table-bordered">
                <thead className="table-light">
                  <tr>
                    <th>#</th>
                    <th>Title</th>
                    <th>Path</th>
                    <th style={{ width: "120px" }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {moduleForm.children.map((c, i) => (
                    <tr key={c._id}>
                      <td>{i + 1}</td>
                      <td>{c.title}</td>
                      <td>{c.path}</td>
                      <td>
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-secondary me-1"
                          style={{ borderRadius: 8 }}
                          onClick={() => openEditChild(c)}
                        >
                          Edit
                        </button>
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-danger"
                          style={{ borderRadius: 8 }}
                          onClick={() => handleDeleteChild(c._id)}
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="mt-3">
            <button
              className="btn btn-secondary me-2"
              onClick={() => setScreen("LIST")}
            >
              Cancel
            </button>
            <button className="btn btn-primary" onClick={saveModule}>
              {isEdit ? "Update Module" : "Save Module"}
            </button>
          </div>
        </>
      )}

      <Offcanvas
        show={childDrawerOpen}
        onHide={closeChildDrawer}
        placement="end"
        scroll
        backdrop="static"
        className="child-drawer-shadow"
      >
        <Offcanvas.Header closeButton className="border-bottom">
          <Offcanvas.Title>
            {isChildEdit ? "Edit child module" : "Add child module"}
          </Offcanvas.Title>
        </Offcanvas.Header>
        <Offcanvas.Body>
          <div className="mb-3 p-2 bg-light rounded border small">
            <div
              className="text-muted text-uppercase fw-semibold mb-1"
              style={{ fontSize: 11 }}
            >
              Path preview
            </div>
            <div className="fw-medium text-break">{drawerPathPreview}</div>
          </div>

          <Form.Group className="mb-3">
            <Form.Label>Parent module</Form.Label>
            <Form.Control type="text" value={drawerParentTitle} disabled readOnly />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Title</Form.Label>
            <Form.Control
              value={childForm.title}
              onChange={(e) =>
                setChildForm({ ...childForm, title: e.target.value })
              }
              placeholder="e.g. Purchase Order"
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Path</Form.Label>
            <Form.Control
              value={childForm.path}
              onChange={(e) =>
                setChildForm({ ...childForm, path: e.target.value })
              }
              placeholder="/app/…"
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Status</Form.Label>
            <div className="d-flex gap-3">
              <Form.Check
                type="radio"
                name="child-status"
                id="child-st-active"
                label="Active"
                checked={childForm.status === STATUS.ACTIVE}
                onChange={() =>
                  setChildForm({ ...childForm, status: STATUS.ACTIVE })
                }
              />
              <Form.Check
                type="radio"
                name="child-status"
                id="child-st-inactive"
                label="Inactive"
                checked={childForm.status !== STATUS.ACTIVE}
                onChange={() =>
                  setChildForm({ ...childForm, status: STATUS.INACTIVE })
                }
              />
            </div>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Icon (optional)</Form.Label>
            <Form.Control
              className="mb-2"
              placeholder="Bootstrap Icons class, e.g. bi-journal-text"
              value={childForm.iconClass}
              onChange={(e) =>
                setChildForm({ ...childForm, iconClass: e.target.value })
              }
            />
            {childForm.iconClass.trim() ? (
              <div className="d-flex align-items-center gap-2 small text-muted">
                Preview:{" "}
                <i
                  className={
                    childForm.iconClass.trim().startsWith("bi")
                      ? childForm.iconClass.trim()
                      : `bi-${childForm.iconClass.trim().replace(/^bi-/, "")}`
                  }
                  style={{ fontSize: "1.25rem" }}
                  aria-hidden
                />
              </div>
            ) : null}
            <Form.Label className="mt-2">Image URL (optional)</Form.Label>
            <Form.Control
              className="mb-2"
              placeholder="https://…"
              value={childForm.iconImageUrl}
              onChange={(e) =>
                setChildForm({ ...childForm, iconImageUrl: e.target.value })
              }
            />
            <Form.Label className="small text-muted">
              Or upload a small image (stored as data URL in the image field, max 120 KB)
            </Form.Label>
            <input
              type="file"
              accept="image/*"
              className="form-control form-control-sm"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (!file) return;
                if (file.size > 120 * 1024) {
                  window.alert("Please choose an image under 120 KB.");
                  e.target.value = "";
                  return;
                }
                const reader = new FileReader();
                reader.onload = () => {
                  setChildForm((f) => ({
                    ...f,
                    iconImageUrl: String(reader.result || ""),
                  }));
                };
                reader.readAsDataURL(file);
              }}
            />
          </Form.Group>

          <Form.Group className="mb-4">
            <Form.Label>Description</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={childForm.content}
              onChange={(e) =>
                setChildForm({ ...childForm, content: e.target.value })
              }
              placeholder="Optional notes or description"
            />
          </Form.Group>

          <div className="d-flex justify-content-end gap-2 pt-2 border-top">
            <button
              type="button"
              className="btn btn-outline-secondary"
              style={{ borderRadius: 8 }}
              onClick={closeChildDrawer}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn btn-primary"
              style={{ borderRadius: 8 }}
              onClick={saveChild}
            >
              {isChildEdit ? "Save changes" : "Save child"}
            </button>
          </div>
        </Offcanvas.Body>
      </Offcanvas>

      <Offcanvas
        show={enumFormOpen}
        onHide={closeEnumDrawer}
        placement="end"
        scroll
        backdrop="static"
        className="child-drawer-shadow"
      >
        <Offcanvas.Header closeButton className="border-bottom">
          <Offcanvas.Title>
            {enumEditingId ? "Edit enum" : "Add enum"}
          </Offcanvas.Title>
        </Offcanvas.Header>
        <Offcanvas.Body>
          <div className="mb-3 p-2 bg-light rounded border small">
            <div
              className="text-muted text-uppercase fw-semibold mb-1"
              style={{ fontSize: 11 }}
            >
              Scope preview
            </div>
            <div className="fw-medium text-break">{enumDrawerScopePreview}</div>
          </div>

          <Form.Group className="mb-3">
            <Form.Label>List filters (context)</Form.Label>
            <Form.Control type="text" value={enumDrawerFilterHint} disabled readOnly />
            <Form.Text className="text-muted">
              New rows start with type and country from the filters above; you can change them
              before saving.
            </Form.Text>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Type</Form.Label>
            <Form.Control
              value={enumForm.type}
              onChange={(e) =>
                setEnumForm({ ...enumForm, type: e.target.value })
              }
              placeholder="e.g. SYS_CAP"
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Code</Form.Label>
            <Form.Control
              value={enumForm.code}
              onChange={(e) =>
                setEnumForm({ ...enumForm, code: e.target.value })
              }
              placeholder="Unique code / key"
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Value (label)</Form.Label>
            <Form.Control
              value={enumForm.value}
              onChange={(e) =>
                setEnumForm({ ...enumForm, value: e.target.value })
              }
              placeholder="Display label"
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Country</Form.Label>
            <Form.Control
              className="text-uppercase"
              maxLength={2}
              value={enumForm.countryCode}
              onChange={(e) =>
                setEnumForm({ ...enumForm, countryCode: e.target.value })
              }
              placeholder="IN"
            />
          </Form.Group>

          <Form.Group className="mb-4">
            <Form.Label>Status</Form.Label>
            <div className="d-flex gap-3">
              <Form.Check
                type="radio"
                name="enum-form-status"
                id="enum-st-active"
                label="Active"
                checked={enumForm.status === "active"}
                onChange={() =>
                  setEnumForm({ ...enumForm, status: "active" })
                }
              />
              <Form.Check
                type="radio"
                name="enum-form-status"
                id="enum-st-inactive"
                label="Inactive"
                checked={enumForm.status !== "active"}
                onChange={() =>
                  setEnumForm({ ...enumForm, status: "inactive" })
                }
              />
            </div>
          </Form.Group>

          <div className="d-flex justify-content-end gap-2 pt-2 border-top">
            <button
              type="button"
              className="btn btn-outline-secondary"
              style={{ borderRadius: 8 }}
              onClick={closeEnumDrawer}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn btn-primary"
              style={{ borderRadius: 8 }}
              onClick={saveEnumRow}
            >
              {enumEditingId ? "Save changes" : "Save enum"}
            </button>
          </div>
        </Offcanvas.Body>
      </Offcanvas>
    </div>
  );
}
